import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import PropTypes from 'prop-types';
import reactHtmlParser from 'react-html-parser';
import officeApi from '../../../api/officeApi';
import { isDesktop } from '../../utils/environment';
import { Tooltip } from '../shared/Tooltip';

import Svg from '../shared/Svg';
import ObjectIcon from '../shared/ObjectIcon';

import colors from '../../utils/colors';
import { PopOutIcon } from '../shared/PopOutIcon';
import { SearchContext } from './SearchContainer';

import * as RootActions from '../../actions/index';
import * as MailActions from '../../actions/mail';
import * as FileActions from '../../actions/files';
import { sanitizeString } from '../../utils/stringUtils';
import { deleteRelatedToTimeRecord } from '../../actions/time';

import './SearchTableItem.scss';

const MAX_SELECTED_RECORDS = 10;

const makeBold = (field, keyword) => {
  if (!keyword || keyword === '' || !field || field === '' || typeof field !== 'string') {
    return field;
  }

  // For clarity sanitize the keyword before the RegExp lookup.
  const sanitizedKeyword = sanitizeString(keyword);

  return (
    field?.replace(new RegExp('(' + sanitizedKeyword + ')', 'gi'), '<b>$1</b>')
  );
};

export const SearchTableItem = ({ item, isSelectedRecord, setRecordToDeselect }) => {
  if (!item) {
    return (
      <div className={'searchTableItem'}>
        <div className="searchTableItem-empty "><i>
            No search results found, please refine your search</i>
        </div>
      </div>
    );
  }

  const context = useContext(SearchContext);
  const dispatch = useDispatch();
  const type = item.type || context.searchObject?.apiName;
  const user = useSelector((state) => state.auth.user);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const recordLink = `${user.salesforceInstanceUrl}/lightning/r/${type}/${item.Id}/view`;
  const objectsAndDisplayFields = useSelector((state) => state.mail.settings?.objectsAndDisplayFields);
  const allowRemoveSavedLocation = useSelector((state) => state.mail.settings?.docrioSettings?.allowRemoveSavedLocation);
  const isMultiSave = useSelector((state) => state.mail.settings?.multiSave);
  const currentDisplayFields = objectsAndDisplayFields?.find((object) => object.apiName === type)?.displayFields.map((field) => field.apiName) || [];
  const isEmailSaved = useSelector((state) => state.mail.isEmailSaved);
  const presave = useSelector((state) => state.mail.presave);
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const filesSaved = useSelector((state) => state.files?.docrioFiles?.filter((file) => file.saved).length > 0);
  const docrioFiles = useSelector((state) => state.files.docrioFiles);
  const isSyncingAttachments = useSelector((state) => state.app.isSyncingAttachments);
  const autoThreadingDefault = useSelector((state) => state.mail.autoThreadingDefault);
  const fileMaps = useSelector((state) => state.files.fileMaps);
  const docrioNamespace = useSelector((state) => state.mail.settings?.override?.docrioNamespace || 'litify_docs');

  const [itemHovered, setItemHovered] = useState(false);
  const [closeDisabled, setCloseDisabled] = useState(false);
  const [isSelected, setIsSelected] = useState(!isSelectedRecord && !!context?.checkedRecords?.[item?.Id]);
  const [removeSavedLocationDisabled, setRemoveSavedLocationDisabled] = useState(false);

  const selectRecord = async () => {
    /* if in compose, force a save here. This is needed to handle the scenario where
     * a user has opened the add-in, and then begins filling out the email. We are not
     * listening to subject updates at this point, so forcing a save here will give us
     * a head start on saving before the save button is pushed.
     */
    if (isComposingEmail && isDesktop()) {
      await officeApi.saveEmail();
    }

    if (context.checkedRecords[item.Id]) {
      setIsSelected(false);
      context.setCheckedRecords((prevState) => {
        const newState = { ...prevState };
        delete newState[item.Id];
        return newState;
      });
    } else {
      if (Object.keys(context.checkedRecords).length === MAX_SELECTED_RECORDS) {
        setIsSelected(false);
        dispatch(RootActions.changeAlertText(`Maximum number of ${MAX_SELECTED_RECORDS} saved locations added.`, 'error'));
      } else {
        setIsSelected(true);
        item.type = type;
        item.autothreading = autoThreadingDefault;
        context.setCheckedRecords((prevState) => ({ ...prevState, [item.Id]: item }));
      }
    }
  };

  useEffect(() => {
    if (isSelectedRecord) {
      const filesSaving = docrioFiles.filter((file) => file.isSaving);
      if (!!filesSaving.length || isSyncingAttachments) {
        setCloseDisabled(true);
      } else if (isComposingEmail && presave) {
        setRemoveSavedLocationDisabled(true);
        setCloseDisabled(true);
      } else {
        setCloseDisabled(false);
        setRemoveSavedLocationDisabled(false);
      }
    }
  }, [docrioFiles, isSyncingAttachments, isSelectedRecord, isComposingEmail, presave]);

  const closeRecord = () => {
    // handle docrio files that were saved to a old selected record
    const updateDocrioFiles = docrioFiles.map((file) => {
      if (isComposingEmail && file.docrioId && file.folderPaths[item.Id]) {
        delete file.folderPaths[item.Id];
        delete file.docrioId;
      }
      return file;
    });

    dispatch(FileActions.setDocrioFiles(updateDocrioFiles));

    const newFileMaps = { ...fileMaps };
    delete newFileMaps[item.Id];
    batch(() => {
      dispatch(MailActions.unselectRecord(item));
      dispatch(FileActions.clearFileMaps(item.Id));
      dispatch(FileActions.setFileMaps(newFileMaps));
      dispatch(deleteRelatedToTimeRecord(item.Id));
    });
    // Handle case where after save, a saved location is added and then removed
    const updatedFiles = [];
    docrioFiles.forEach((file) => {
      const filteredSelectedRecords = Object.values(selectedRecords).filter((record) => {
        return !!record.attachments?.find((attachment) => {
          return attachment[`${docrioNamespace}__File_Data_Hash__c`] === file.hash;
        });
      }).length;
      // if filteredSelectedRecords is 0 then the file is not saved to any selected records
      const fileSavedToAllSelectedRecords = filteredSelectedRecords > 0 && filteredSelectedRecords === Object.keys(file.folderPaths).length;
      if (fileSavedToAllSelectedRecords) {
        file.saved = true;
        file.toSave = false;
      }
      updatedFiles.push(file);
    });
    if (updatedFiles.length > 0) {
      dispatch(FileActions.setDocrioFiles(updatedFiles));
    }
  };

  const showErrorIcon = () => isSelectedRecord && item.emailMessageError;
  const showPopoutIcon = () => itemHovered || isSelectedRecord;
  const showPlusIcon = () => !isSelectedRecord;
  const showCloseIcon = () => isSelectedRecord && allowRemoveSavedLocation || isSelectedRecord && (!isEmailSaved[item.Id] && !filesSaved && !(isComposingEmail && presave));
  const closeTooltip = () => {
    if (closeDisabled) {
      if (removeSavedLocationDisabled) {
        return <span>Unable to remove saved location until after email has been sent</span>;
      } else {
        return <span>Cannot clear while saving</span>;
      }
    }
    return <span>Clear selection</span>;
  };

  const initiateCloseRecord = () => {
    if ((isEmailSaved[item.Id] || filesSaved) && !presave) {
      setRecordToDeselect(item.Id);
    } else {
      closeRecord();
    }
  };

  const disablePlusIcon = (!isMultiSave && Object.keys(context.checkedRecords || {}).length === 1 && !context.checkedRecords[item.Id]);

  // toggle between selected and unselected
  const handlePlusSignClick = () => {
    onPlusClick();
  };

  const [itemClassName, onPlusClick] = isSelectedRecord ?
    ['searchTableItem-selectedRecord', () => {}] :
    ['searchTableItem searchTableItem-highlight', selectRecord];


  return (
    <div className={itemClassName} onMouseEnter={() => setItemHovered(true)} onMouseLeave={() => setItemHovered(false)}>
      {currentDisplayFields.length > 0 ? <>
        <ObjectIcon className={'searchTableItem-itemImage'} objectApiName={type} />
        <div className="searchTableItem-detailsContainer" >
          <Tooltip textContent={item[currentDisplayFields[0]]}>
            <div className="searchTableItem-itemName">{reactHtmlParser(makeBold(item[currentDisplayFields[0]] || '', context.searchQuery))}</div>
          </Tooltip>
          {currentDisplayFields.length > 1 && currentDisplayFields.slice(1).map((displayField) => {
            return (
              <Tooltip key={`${displayField}-${item[displayField]}`} textContent={`${item[displayField] || ''}`}>
                <div className="searchTableItem-details">
                  {reactHtmlParser(makeBold(item[displayField], context.searchQuery))}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </> :
        <>
          <div className="searchTableItem-detailsContainer">
            {(item['Name'] ? item['Name'] : item['Id']) + ' • ' + item['type']}
            <div className="searchTableItem-details" >Display fields not configured</div>
          </div>
        </>
      }
      {(showPopoutIcon() || showCloseIcon() || showPlusIcon() || showErrorIcon()) &&
        <div className='searchTableItem-iconsContainer'>
          {showErrorIcon() && <Svg data="../assets/error.svg" fill='#C23934' className='searchTableItem-errorIcon' tooltipContent={<span>{item?.emailMessageError}</span>}/>}
          {showPopoutIcon() && <PopOutIcon link={recordLink} className={isEmailSaved[item.Id] || filesSaved || presave ? 'searchTableItem-removeMarginRight' : ''}/>}
          {showPlusIcon() && <Svg
            className="searchTableItem-plusIcon"
            data-testid={`save-location-search-result-plus-${item.Id}`}
            data={isSelected ?
                (isEmailSaved[item.Id] || selectedRecords?.[item.Id]?.attachments ?
                  '/assets/checkedInactive.svg' :
                  '../assets/checkedActive.svg') :
                (disablePlusIcon ?
                  '../assets/plusSignInactive.svg' :
                '../assets/plusSignActive.svg')
            }
            onClickAction={ (!isEmailSaved[item.Id] && !selectedRecords?.[item.Id]?.attachments && !disablePlusIcon) && handlePlusSignClick }
          />}
          {showCloseIcon() && <Svg
            className="searchTableItem-closeIcon"
            data="../assets/close.svg"
            fill={ colors.searchGrey }
            hoverable
            hoverFill={ closeDisabled ? colors.searchGrey : colors.iconHover }
            tooltipContent={ closeTooltip() }
            onClickAction={ closeDisabled ? (() => {}) : initiateCloseRecord } // return to recently viewed records
            disabled={ closeDisabled }
          />}
        </div>
      }
    </div>
  );
};

SearchTableItem.propTypes = {
  item: PropTypes.object,
  isSelectedRecord: PropTypes.bool,
  setRecordToDeselect: PropTypes.func,
};
